import { Row } from "@tanstack/react-table";
import { Edit, FolderArchive, Trash2 } from "lucide-react";

import { driveTypes } from "@/constants/constant-values/default-values.constant";
import { CopyOptions, DriveItem, TDriveType } from "@/hooks/useDriveCopy";
import { MoveOptions } from "@/hooks/useDriveMove";
import { IDrive } from "@/interfaces/project-register.interface";
import {
  fileImagePreviewer,
  openFilePreviewInNewTab,
} from "@/lib/file-image.utils";

export const prepareDriveItem = (row: Row<IDrive>): DriveItem => {
  return {
    id: row.original.id,
    type: row.original.type === "dir" ? "directory" : "file",
  };
};

// Shared function to handle moving items
export const handleMoveItem = async ({
  itemId,
  itemType,
  folderId,
  previousDir,
  type,
  ID,
  moveDriveItem,
}: {
  itemId: string;
  itemType: string;
  folderId: string | null;
  previousDir: string | null;
  type: string;
  ID: string;
  moveDriveItem: (options: MoveOptions) => Promise<{
    success: boolean;
    wasCancelled?: boolean;
  }>;
}): Promise<boolean> => {
  if (!previousDir || !folderId) {
    return false;
  }

  const item: DriveItem = {
    id: itemId,
    type: itemType === "dir" ? "directory" : "file",
  };

  const sourceDirectoryId = folderId;
  const targetItemId = previousDir;

  try {
    const result = await moveDriveItem({
      item,
      driveType: type as TDriveType,
      sourceItemId: ID,
      sourceDirectoryId,
      targetItemId,
    });

    return result.success;
  } catch (error) {
    return false;
  }
};

export const getActionOptions = ({
  row,
  folderId,
  isManager,
  type,
  handleDelete,
  handleRename,
  copyToTaskAndClaims,
  copyToProject,
  copyToProjectRegister,
  projectId,
  leadsId,
}: {
  row: Row<IDrive>;
  folderId: string | null;
  isManager: boolean;
  type: string;
  handleDelete: (id: string, type: string, typeId: string) => void;
  handleRename: (item: IDrive) => void;
  copyToTaskAndClaims: (options: CopyOptions) => Promise<void>;
  copyToProject: (options: CopyOptions) => Promise<void>;
  copyToProjectRegister: (options: CopyOptions) => Promise<void>;
  projectId: string;
  leadsId: string;
  previousDir?: string | null;
  moveDriveItem?: (options: MoveOptions) => Promise<{
    success: boolean;
    wasCancelled?: boolean;
  }>;
  ID?: string;
}): ActionButtonDataTypes[] => {
  if (row.original.empty) {
    return [];
  }

  const actions: ActionButtonDataTypes[] = [
    {
      icon: Trash2,
      name: "Delete",
      isTrash: true,
      onclick: (): void =>
        handleDelete(
          row.original.id,
          row.original.type,
          type === driveTypes.PROJECT_REGISTER ? leadsId : projectId,
        ),
    },
    {
      icon: Edit,
      name: "Rename",
      onclick: (): void => {
        handleRename(row.original);
      },
    },
  ];

  if (!isManager || folderId) {
    return actions;
  }

  const driveItem = prepareDriveItem(row);
  const sourceId = type === driveTypes.PROJECT_REGISTER ? leadsId : projectId;

  // Define copy actions in a config array for easier extension and less repetition
  const copyActionsConfig = [
    {
      condition: type === driveTypes.PROJECT,
      actions: [
        {
          icon: FolderArchive,
          name: "Copy to Task and Claims",
          handler: copyToTaskAndClaims,
          targetId: projectId,
        },
        {
          icon: FolderArchive,
          name: "Copy To Project Register",
          handler: copyToProjectRegister,
          targetId: leadsId,
        },
      ],
    },
    {
      condition: type === driveTypes.PROJECT_REGISTER,
      actions: [
        {
          icon: FolderArchive,
          name: "Copy to Task and Claims",
          handler: copyToTaskAndClaims,
          targetId: projectId,
        },
        {
          icon: FolderArchive,
          name: "Copy To Project",
          handler: copyToProject,
          targetId: projectId,
        },
      ],
    },
    {
      condition:
        type !== driveTypes.PROJECT && type !== driveTypes.PROJECT_REGISTER,
      actions: [
        {
          icon: FolderArchive,
          name: "Copy To Project",
          handler: copyToProject,
          targetId: projectId,
        },
        {
          icon: FolderArchive,
          name: "Copy To Project Register",
          handler: copyToProjectRegister,
          targetId: leadsId,
        },
      ],
    },
  ];

  copyActionsConfig.forEach((config) => {
    if (config.condition) {
      config.actions.forEach(({ icon, name, handler, targetId }) => {
        actions.push({
          icon,
          name,
          onclick: async (): Promise<void> => {
            const copyOptions: CopyOptions = {
              sourceItem: driveItem,
              sourceDriveType: type as TDriveType,
              sourceId,
              targetId,
              targetFolderId: folderId || undefined,
            };

            await handler(copyOptions);
          },
        });
      });
    }
  });

  return actions;
};

export const handleFileClick = ({
  url,
  isEmpty,
  folderId,
  previousDir,
  set,
  reset,
}: {
  url: string;
  isEmpty?: boolean;
  folderId?: string | null;
  previousDir?: string | null;
  set?: (id: string) => void;
  reset?: () => void;
}): void => {
  if (folderId && isEmpty) {
    if (previousDir && set) {
      set(previousDir);
    } else if (reset) {
      reset();
    }

    return;
  }

  if (url) {
    openFilePreviewInNewTab(url);
  }
};

export const filterDriveData = (
  data: IDrive[],
  folderId: string | null,
): IDrive[] => {
  // If we're in a specific folder, filter items for that folder
  if (folderId) {
    const backItem: IDrive = {
      id: "",
      name: "...",
      type: "document",
      size: 0,
      empty: true, // This indicates it's a special item
      uploader: {
        firstName: "",
        lastName: "",
      },
      url: "",
      parentDirectoryId: null,
    };
    const folderItems = data.filter((item) => {
      const parentId = item.parentDirectoryId;

      return parentId === folderId;
    });

    return [backItem, ...folderItems];
  }

  // If we're in root directory, only show root-level items

  return data.filter((item) => {
    const parentId = item.parentDirectoryId;

    return !parentId || parentId === null;
  });
};

export const renderFilePreviewer = (
  item: IDrive,
  onClick: () => void,
  className = "w-16 h-16",
): React.ReactNode => {
  return fileImagePreviewer({
    file: item.url,
    className,
    config: {
      onClick,
    },
  });
};
